import React, { useEffect, useMemo, useState } from "react";
import type { GeoSearchByIdDTO, GeoIdName } from "../../types/SearchFilter";
import {
    fetchAdmin1ByCountry,
    fetchAdmin2ByAdmin1,
    fetchCitiesByAdmin2,
    fetchCountries,
    fetchPostalCodesByCity,
    reverseGeocode,
    ReverseGeocode,
} from "../../api/GeoDataAPI";
import { logApiError, getUserMessage } from "../../api/apiError";
import { deepestName, getBrowserPosition, loadLastLocation, saveLastLocation } from "../../utils/lastLocation";
import { LEVEL_LABEL, NEXT_LEVEL } from "./GeoAreaPicker";

type IdName = { id: string | number; name: string };
type Postal = { id: string | number; postalCode: string; placeName?: string };

interface Props {
    token: string;
    onChange: (geo: GeoSearchByIdDTO | null) => void;
}

export default function GeoSearchSelector({ token, onChange }: Props) {
    const [countries, setCountries] = useState<IdName[]>([]);
    const [admin1List, setAdmin1List] = useState<IdName[]>([]);
    const [admin2List, setAdmin2List] = useState<IdName[]>([]);
    const [cities, setCities] = useState<IdName[]>([]);
    const [postalCodes, setPostalCodes] = useState<Postal[]>([]);

    const [countryId, setCountryId] = useState("");
    const [admin1Id, setAdmin1Id] = useState("");
    const [admin2Id, setAdmin2Id] = useState("");
    const [cityId, setCityId] = useState("");
    const [postalCodeId, setPostalCodeId] = useState("");

    const [error, setError] = useState<string | null>(null);
    const [locating, setLocating] = useState(false);
    const [lastLoc] = useState<ReverseGeocode | null>(loadLastLocation);

    // deepest selection wins
    const geo = useMemo<GeoSearchByIdDTO | null>(() => {
        const mk = (geoIdName: GeoIdName, idStr: string): GeoSearchByIdDTO | null => {
            if (!idStr) return null;
            const id = Number(idStr);
            if (Number.isNaN(id)) return null;
            return { geoSearchType: "geoId", geoIdName, id };
        };

        return (
            mk("PostalCode", postalCodeId) ??
            mk("City", cityId) ??
            mk("Admin2", admin2Id) ??
            mk("Admin1", admin1Id) ??
            mk("Country", countryId) ??
            null
        );
    }, [countryId, admin1Id, admin2Id, cityId, postalCodeId]);

    useEffect(() => {
        onChange(geo);
    }, [geo, onChange]);

    // load countries
    useEffect(() => {
        (async () => {
            try {
                setError(null);
                const data = await fetchCountries(token);
                setCountries(data ?? []);
            } catch (e: any) {
                logApiError(e);
                setError(getUserMessage(e));
            }
        })();
    }, [token]);

    const resetBelowCountry = () => {
        setAdmin1Id(""); setAdmin2Id(""); setCityId(""); setPostalCodeId("");
        setAdmin1List([]); setAdmin2List([]); setCities([]); setPostalCodes([]);
    };

    const resetBelowAdmin1 = () => {
        setAdmin2Id(""); setCityId(""); setPostalCodeId("");
        setAdmin2List([]); setCities([]); setPostalCodes([]);
    };

    const resetBelowAdmin2 = () => {
        setCityId(""); setPostalCodeId("");
        setCities([]); setPostalCodes([]);
    };

    const resetBelowCity = () => {
        setPostalCodeId(""); setPostalCodes([]);
    };

    const handleCountryChange = async (v: string) => {
        setCountryId(v);
        resetBelowCountry();
        setError(null);
        if (!v) return;

        try {
            const data = await fetchAdmin1ByCountry(token, v);
            setAdmin1List(data ?? []);
        } catch (e: any) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };

    const handleAdmin1Change = async (v: string) => {
        setAdmin1Id(v);
        resetBelowAdmin1();
        setError(null);
        if (!v) return;

        try {
            const data = await fetchAdmin2ByAdmin1(token, v);
            setAdmin2List(data ?? []);
        } catch (e: any) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };

    const handleAdmin2Change = async (v: string) => {
        setAdmin2Id(v);
        resetBelowAdmin2();
        setError(null);
        if (!v) return;

        try {
            const data = await fetchCitiesByAdmin2(token, v);
            setCities(data ?? []);
        } catch (e: any) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };

    const handleCityChange = async (v: string) => {
        setCityId(v);
        resetBelowCity();
        setError(null);
        if (!v) return;

        try {
            const data = await fetchPostalCodesByCity(token, v);
            setPostalCodes(data ?? []);
        } catch (e: any) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };

    const clear = () => {
        setCountryId("");
        resetBelowCountry();
        setError(null);
        onChange(null);
    };

    // Applies a resolved chain (from geolocation or the remembered location):
    // sets each level's id and loads the next level's options in sequence.
    const applyChain = async (chain: ReverseGeocode) => {
        if (!chain.country) return;
        setError(null);
        resetBelowCountry();
        setCountryId(String(chain.country.id));
        try {
            setAdmin1List((await fetchAdmin1ByCountry(token, String(chain.country.id))) ?? []);
            if (!chain.admin1) return;
            setAdmin1Id(String(chain.admin1.id));
            setAdmin2List((await fetchAdmin2ByAdmin1(token, String(chain.admin1.id))) ?? []);
            if (!chain.admin2) return;
            setAdmin2Id(String(chain.admin2.id));
            setCities((await fetchCitiesByAdmin2(token, String(chain.admin2.id))) ?? []);
            if (!chain.city) return;
            setCityId(String(chain.city.id));
            setPostalCodes((await fetchPostalCodesByCity(token, String(chain.city.id))) ?? []);
        } catch (e: any) {
            logApiError(e);
            setError(getUserMessage(e));
        }
    };

    const useMyLocation = async () => {
        setLocating(true);
        setError(null);
        try {
            const pos = await getBrowserPosition();
            const chain = await reverseGeocode(token, pos.lat, pos.lon);
            await applyChain(chain);
        } catch (e: any) {
            setError(e?.message === "denied"
                ? "Location permission was denied."
                : "Could not determine your location.");
        } finally {
            setLocating(false);
        }
    };

    // Remember the selection (names looked up from the loaded lists) so other
    // pickers / next visits can offer it back.
    useEffect(() => {
        if (!countryId) return;
        const nameIn = (list: IdName[], id: string) =>
            list.find((x) => String(x.id) === id)?.name;
        const countryName = nameIn(countries, countryId);
        if (!countryName) return;
        const admin1Name = admin1Id ? nameIn(admin1List, admin1Id) : undefined;
        const admin2Name = admin2Id ? nameIn(admin2List, admin2Id) : undefined;
        const cityName = cityId ? nameIn(cities, cityId) : undefined;
        saveLastLocation({
            country: { id: Number(countryId), name: countryName },
            admin1: admin1Name ? { id: Number(admin1Id), name: admin1Name } : undefined,
            admin2: admin2Name ? { id: Number(admin2Id), name: admin2Name } : undefined,
            city: cityName ? { id: Number(cityId), name: cityName } : undefined,
        });
    }, [countryId, admin1Id, admin2Id, cityId, countries, admin1List, admin2List, cities]);

    // Hint rendered just below the deepest-set field.
    const hintAt = (level: GeoIdName) => {
        const current = geo?.geoIdName ?? "Country";
        if (current !== level) return null;
        const nameIn = (list: { id: string | number; name?: string }[], id: string) =>
            list.find((x) => String(x.id) === id)?.name;
        let text: string;
        if (!geo) {
            text = "No location filter — choose where to search.";
        } else {
            const name =
                geo.geoIdName === "PostalCode"
                    ? postalCodes.find((p) => String(p.id) === postalCodeId)?.postalCode
                    : geo.geoIdName === "City" ? nameIn(cities, cityId)
                    : geo.geoIdName === "Admin2" ? nameIn(admin2List, admin2Id)
                    : geo.geoIdName === "Admin1" ? nameIn(admin1List, admin1Id)
                    : nameIn(countries, countryId);
            const scope = LEVEL_LABEL[geo.geoIdName] ?? geo.geoIdName;
            const next = NEXT_LEVEL[geo.geoIdName];
            text = `This will search in ${name ?? geo.geoIdName} (${scope}).`
                + (next ? ` You can further narrow the search to ${next}.` : "");
        }
        return (
            <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", fontStyle: "italic" }}>
                {text}
            </div>
        );
    };

    return (
        <div className="geo-selector">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                <h4 style={{ margin: 0 }}>Location Filter (optional)</h4>
                <button type="button" onClick={clear} className="btn-sm btn-ghost">Clear</button>
            </div>

            {error && <div className="status-error" style={{ marginTop: "0.5rem" }}>{error}</div>}

            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                <button type="button" onClick={useMyLocation} disabled={locating}>
                    {locating ? "Locating…" : "📍 Use my location"}
                </button>
                {lastLoc && deepestName(lastLoc) && (
                    <button type="button" onClick={() => applyChain(lastLoc)} disabled={locating}>
                        ↩ Last: {deepestName(lastLoc)}
                    </button>
                )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.75rem" }}>
                {/* Country */}
                <label>
                    <div>Country</div>
                    <select value={countryId} onChange={(e) => handleCountryChange(e.target.value)}>
                        <option value="">Any</option>
                        {countries.map((c) => (
                            <option key={String(c.id)} value={String(c.id)}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </label>
                {hintAt("Country")}

                {/* Admin1 (only after Country selected) */}
                {countryId && (
                    <label>
                        <div>Admin1</div>
                        <select value={admin1Id} onChange={(e) => handleAdmin1Change(e.target.value)}>
                            <option value="">Any</option>
                            {admin1List.map((a) => (
                                <option key={String(a.id)} value={String(a.id)}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {hintAt("Admin1")}

                {/* Admin2 (only after Admin1 selected) */}
                {admin1Id && (
                    <label>
                        <div>Admin2</div>
                        <select value={admin2Id} onChange={(e) => handleAdmin2Change(e.target.value)}>
                            <option value="">Any</option>
                            {admin2List.map((a) => (
                                <option key={String(a.id)} value={String(a.id)}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {hintAt("Admin2")}

                {/* City (only after Admin2 selected) */}
                {admin2Id && (
                    <label>
                        <div>City</div>
                        <select value={cityId} onChange={(e) => handleCityChange(e.target.value)}>
                            <option value="">Any</option>
                            {cities.map((c) => (
                                <option key={String(c.id)} value={String(c.id)}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {hintAt("City")}

                {/* Postal Code (only after City selected) */}
                {cityId && (
                    <label>
                        <div>Postal Code</div>
                        <select value={postalCodeId} onChange={(e) => setPostalCodeId(e.target.value)}>
                            <option value="">Any</option>
                            {postalCodes.map((p) => (
                                <option key={String(p.id)} value={String(p.id)}>
                                    {p.postalCode} {p.placeName ? `(${p.placeName})` : ""}
                                </option>
                            ))}
                        </select>
                    </label>
                )}
                {hintAt("PostalCode")}
            </div>
        </div>
    );
}